import * as signalR from '@microsoft/signalr';
import { UrlKeyDto } from '@trucktrax/trucktrax-ts-common';
import recordLog from '../../util/errorUtil';
import { acknowledgeMessage, updateMessages } from '../../store/actions/messageActions';
import { store } from '../../Main';
import { getInboxMessages } from '../messagesService';
import { JOIN_GROUP, RECEIVE_MESSAGE } from '../../constants/appConstants';
import {
  ACKNOWLEDGE_MESSAGE_GROUP,
  NEW_MESSAGE_GROUP,
  MESSAGING_SOCKET,
} from '../../constants/apiConstants';
import { getBridgeBaseUrl } from '../../util/apiUtil';
import { getTokens, isExpired } from '../../util/authUtil';
import { ensureFreshAccessToken, RefreshOutcome } from '../requestService';
import { ConnectionState, isAuthFailureError, ReconnectScheduler } from '../../util/socketUtil';
import { allPromisesResolved } from '../../util/appUtil';

type Subscription = { url?: string; unsubscribe: () => void };

export class MessagingSocket {
  connectionState: ConnectionState;

  hubConnection: signalR.HubConnection | null;

  private regionUrl: string | null;

  private shouldReconnectOnClose: boolean = true;

  selectedProductLine?: string;

  /**
   * The dispatcher id of the last successful (or attempted) connection. Stored
   * so {@link reconnect} can re-invoke {@link connect} without callers having
   * to re-supply arguments.
   */
  private dispatcherId?: string;

  /**
   * The subscription list passed to the last {@link connect}. Preserved across
   * reconnects so subscription lifecycle hooks remain wired up.
   */
  private currentSubscriptions?: Subscription[];

  /**
   * Owns backoff state (attempt counter + pending timer) for this socket.
   */
  private reconnectScheduler = new ReconnectScheduler();

  constructor() {
    this.connectionState = ConnectionState.Disconnected;
    this.hubConnection = null;
    this.regionUrl = null;
  }

  static onResponse = (response: string, regionUrl?: string) => {
    const { message, group } = JSON.parse(response);
    const { selectedProductLine } = store.getState();
    if (group === ACKNOWLEDGE_MESSAGE_GROUP) {
      store.dispatch(acknowledgeMessage(message));
    } else if (group === NEW_MESSAGE_GROUP) {
      if (message.productLine !== selectedProductLine) {
        return;
      }
      store.dispatch(updateMessages(message, regionUrl ?? ''));
    }
  };

  async connect(
    dispatcherId: string,
    currentRegion?: UrlKeyDto,
    currentSubscriptions?: Subscription[],
  ) {
    const { tenantId: tenant, selectedProductLine } = store.getState();

    let { accessToken } = getTokens();
    // do not try to connect if we're not logged in
    if (!accessToken) {
      return;
    }

    // Remember the args before any early return so a scheduled reconnect can
    // recover even when this first attempt bails on an expired token.
    this.dispatcherId = dispatcherId;
    this.currentSubscriptions = currentSubscriptions;

    // An expired token 403s on /negotiate and spins the reconnect loop, so
    // refresh first. transient -> keep backing off; invalid -> user logged out.
    if (isExpired(accessToken)) {
      const refreshOutcome = await ensureFreshAccessToken();
      switch (refreshOutcome) {
        case RefreshOutcome.Refreshed:
          break;
        case RefreshOutcome.Transient:
          this.reconnect();
          return;
        case RefreshOutcome.Invalid:
          return;
        default:
          return;
      }
      accessToken = getTokens().accessToken;
      if (!accessToken) {
        return;
      }
    }

    if (!currentRegion?.url) {
      return;
    }

    const regionUrl = currentRegion.url.trim();
    const isRedundantConnectionAttempt =
      this.regionUrl === regionUrl &&
      (this.connectionState === ConnectionState.Connecting ||
        this.connectionState === ConnectionState.Connected);
    if (isRedundantConnectionAttempt) {
      return;
    }

    this.connectionState = ConnectionState.Connecting;
    this.regionUrl = regionUrl;

    const MESSAGING_SOCKET_URL = `${getBridgeBaseUrl()}${MESSAGING_SOCKET}`;

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${MESSAGING_SOCKET_URL}?tk=${accessToken}`, {
        accessTokenFactory: () => accessToken!,
      })
      .configureLogging(signalR.LogLevel.None)
      .build();

    this.hubConnection.serverTimeoutInMilliseconds = 30_000;
    this.hubConnection.on(RECEIVE_MESSAGE, (message: string) => {
      MessagingSocket.onResponse(message, regionUrl);
    });
    const localHubConnection = this.hubConnection;
    this.shouldReconnectOnClose = true;

    this.hubConnection.onclose(_ => {
      // Mark the connection as down and route through reconnect() so the
      // exponential backoff applies. Directly calling connect() here (the prior
      // behavior) produced a tight loop when the server accepted then quickly
      // dropped the socket, because start() kept succeeding.
      const matchingConnection = this.hubConnection === localHubConnection;
      if (!matchingConnection) {
        return;
      }

      this.clearHubConnectionState(true);
      if (this.shouldReconnectOnClose) {
        this.reconnect();
      }
    });

    try {
      await localHubConnection.start();
      // TODO: Remove tenant from websocket group (work is done in future card)
      const groupJoinResponses = await Promise.allSettled(
        [ACKNOWLEDGE_MESSAGE_GROUP, NEW_MESSAGE_GROUP]
          .map(group => `${group}${tenant}/${regionUrl}`)
          .map(group => localHubConnection.invoke(JOIN_GROUP, group)),
      );

      const allGroupsConnected = allPromisesResolved(groupJoinResponses);
      if (!allGroupsConnected) {
        throw new Error('Failed to establish all group connections forcing reconnect');
      }
      const matchingConnection = this.hubConnection === localHubConnection;
      if (matchingConnection) {
        this.connectionState = ConnectionState.Connected;
        // Full handshake + group joins succeeded, so reset backoff. A later drop
        // should recover promptly rather than inherit stale delay.
        this.reconnectScheduler.reset();
        // retrieving all messages after connection to account for new messages received
        // from app refresh to websocket connection success
        store.dispatch<any>(
          getInboxMessages(undefined, undefined, currentRegion, selectedProductLine),
        );
      }
    } catch (err) {
      recordLog('warn', 'Messaging Socket', err);
      const matchingConnection = this.hubConnection === localHubConnection;
      if (matchingConnection) {
        this.clearHubConnectionState(true);
        // A 401/403 here is the server refusing a token the client thinks is
        // valid (e.g. auth outage rejecting good tokens, DEV-4179): refresh,
        // then retry -- unless the session is definitively gone.
        if (isAuthFailureError(err)) {
          const refreshOutcome = await ensureFreshAccessToken();
          if (refreshOutcome === RefreshOutcome.Invalid) {
            return;
          }
        }
        this.reconnect();
      }
    }
  }

  /**
   * Queues the next reconnect attempt using capped exponential backoff.
   * Re-reads the currently selected region from the Redux store at fire time
   * so that a region change during the outage reconnects to the new region
   * rather than the stale one captured when the socket was first opened.
   */
  reconnect() {
    // Never reconnect over a live/opening socket -- a connect() that lands
    // Connected during the 403-catch refresh await must not be orphaned (PR #1217).
    if (
      this.connectionState === ConnectionState.Connecting ||
      this.connectionState === ConnectionState.Connected
    ) {
      return;
    }

    if (
      this.connectionState === ConnectionState.Reconnecting &&
      this.reconnectScheduler.isPending
    ) {
      return;
    }

    this.connectionState = ConnectionState.Reconnecting;
    this.reconnectScheduler.schedule(async () => {
      if (!this.dispatcherId) {
        return;
      }
      const { currentRegion: region } = store.getState();
      if (this.reconnectScheduler.currentAttempt >= 2) {
        // Rebuild the (possibly wedged) hub but preserve backoff -- resetting
        // it here pins persistent failures at a 2-4s retry storm (DEV-4179).
        await this.disconnect(true, { preserveBackoff: true });
        await this.connect(this.dispatcherId, region, this.currentSubscriptions);
        return;
      }
      await this.connect(this.dispatcherId, region, this.currentSubscriptions);
    });
  }

  async disconnect(destroyHub?: boolean, { preserveBackoff = false } = {}): Promise<void> {
    this.shouldReconnectOnClose = false;
    if (!preserveBackoff) {
      this.reconnectScheduler.reset();
    }

    try {
      if (this.hubConnection) {
        await this.hubConnection.stop();
      }
    } catch (_: unknown) {
      // Suppress errors from stop()
    } finally {
      this.clearHubConnectionState(destroyHub);
      this.shouldReconnectOnClose = true;
    }
  }

  private clearHubConnectionState(destroyHub: boolean = false): void {
    if (this.connectionState !== ConnectionState.Disconnected) {
      this.connectionState = ConnectionState.Disconnected;
    }

    if (destroyHub && this.hubConnection) {
      this.hubConnection.off(RECEIVE_MESSAGE);
      this.hubConnection = null;
    }

    this.regionUrl = null;
  }

  public async attemptConnectionChange(
    region?: UrlKeyDto,
    currentSubscriptions?: Subscription[],
  ): Promise<void> {
    if (!region?.url) {
      return;
    }

    const regionUrl = region.url.trim();
    if (this.regionUrl === regionUrl && this.connectionState === ConnectionState.Connected) {
      return;
    }

    const { userUrl } = store.getState();
    const dispatcherId = this.dispatcherId ?? userUrl ?? '';
    const subscriptions = currentSubscriptions ?? this.currentSubscriptions;

    await this.disconnect(true);
    await this.connect(dispatcherId, region, subscriptions);
  }
}

export default new MessagingSocket();
